import { useCallback, useMemo, useState } from 'react'

import AddIcon from '@mui/icons-material/Add'
import { Box, Button, Stack } from '@mui/material'
import { Scrollbar } from '@/components/Scrollbar'

import { DGELicensingPeriod, StreamingLicensingPeriod } from './LicensingPeriod.dge'

type Props = {
  defaultValue?: StreamingLicensingPeriod[]
  onChange?: (periods: StreamingLicensingPeriod[], isValid: boolean) => void
  height?: number | string
  autoBooking?: boolean
}

type PeriodWithId = StreamingLicensingPeriod & { id: string }

const isPeriodValid = (period: StreamingLicensingPeriod, autoBooking?: boolean): boolean => {
  const baseValid = !!(period.year && period.startDate && period.endDate)
  if (autoBooking) {
    return baseValid
  }
  return baseValid && (period.maxEvents ?? 0) >= 0 && (period.purchasedEvents ?? 0) >= 0
}

export const DGELicensingPeriods = ({
  defaultValue = [],
  onChange,
  height = 400,
  autoBooking = false
}: Props) => {
  const [periods, setPeriods] = useState<PeriodWithId[]>(() =>
    defaultValue.map((period, index) => ({
      ...period,
      id: `period-${Date.now()}-${index}`
    }))
  )

  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [showValidationErrors, setShowValidationErrors] = useState<Set<string>>(new Set())
  const [addButtonDisabled, setAddButtonDisabled] = useState(false)

  const notifyParent = useCallback(
    (newPeriods: PeriodWithId[]) => {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const periodsForParent = newPeriods.map(({ id, ...period }) => period)
      const allValid = newPeriods.every((period) => isPeriodValid(period, autoBooking))
      onChange?.(periodsForParent, allValid)
    },
    [onChange, autoBooking]
  )

  const handleAddPeriod = useCallback(() => {
    const hasInvalidPeriods = periods.some((period) => !isPeriodValid(period, autoBooking))

    if (hasInvalidPeriods) {
      const invalidPeriods = periods.filter((period) => !isPeriodValid(period, autoBooking))
      const invalidPeriodIds = new Set(invalidPeriods.map((period) => period.id))
      setShowValidationErrors(invalidPeriodIds)
      setAddButtonDisabled(true)

      if (invalidPeriods.length > 0) {
        setExpandedId(invalidPeriods[0].id)
      }

      return
    }

    const newPeriod: PeriodWithId = {
      year: '',
      startDate: '',
      endDate: '',
      maxEvents: 0,
      purchasedEvents: 0,
      id: `period-${Date.now()}`
    }

    const newPeriods = [newPeriod, ...periods]
    setPeriods(newPeriods)
    setExpandedId(newPeriod.id)
    notifyParent(newPeriods)
  }, [periods, notifyParent, autoBooking])

  const handleDeletePeriod = useCallback(
    (index: number) => {
      const periodToDelete = periods[index]
      const newPeriods = periods.filter((_, i) => i !== index)
      setPeriods(newPeriods)

      if (expandedId === periodToDelete.id) {
        setExpandedId(null)
      }

      setShowValidationErrors((prev) => {
        const newSet = new Set(prev)
        newSet.delete(periodToDelete.id)
        return newSet
      })

      const allRemainingPeriodsValid = newPeriods.every((period) =>
        isPeriodValid(period, autoBooking)
      )
      if (allRemainingPeriodsValid && addButtonDisabled) {
        setAddButtonDisabled(false)
      }

      notifyParent(newPeriods)
    },
    [periods, expandedId, notifyParent, addButtonDisabled, autoBooking]
  )

  const handlePeriodChange = useCallback(
    (index: number, updatedPeriod: Partial<StreamingLicensingPeriod>) => {
      const newPeriods = [...periods]
      newPeriods[index] = { ...newPeriods[index], ...updatedPeriod }
      setPeriods(newPeriods)

      const updatedFullPeriod = newPeriods[index]
      if (isPeriodValid(updatedFullPeriod, autoBooking)) {
        setShowValidationErrors((prev) => {
          const newSet = new Set(prev)
          newSet.delete(updatedFullPeriod.id)
          return newSet
        })
      }

      const allPeriodsValid = newPeriods.every((period) => isPeriodValid(period, autoBooking))
      if (allPeriodsValid && addButtonDisabled) {
        setAddButtonDisabled(false)
      }

      notifyParent(newPeriods)
    },
    [periods, notifyParent, addButtonDisabled, autoBooking]
  )

  const handleExpandedChange = useCallback((periodId: string, expanded: boolean) => {
    setExpandedId(expanded ? periodId : null)
  }, [])

  const usedYearsByPeriod = useMemo(() => {
    const usedYearsMap = new Map<string, Set<string>>()

    periods.forEach((period) => {
      const usedYears = new Set(
        periods.filter((p) => p.id !== period.id && p.year).map((p) => p.year)
      )
      usedYearsMap.set(period.id, usedYears)
    })

    return usedYearsMap
  }, [periods])

  return (
    <Box
      sx={{
        height,
        minHeight: height,
        display: 'flex',
        flexDirection: 'column',
        mr: -1
      }}
    >
      <Scrollbar style={{ height: '100%' }}>
        <Stack spacing={1} sx={{ pr: 1 }}>
          {periods.map((period, index) => {
            const isValid = isPeriodValid(period, autoBooking)
            const shouldShowErrors = showValidationErrors.has(period.id)
            const usedYearsForThisPeriod = usedYearsByPeriod.get(period.id) || new Set()

            return (
              <DGELicensingPeriod
                key={period.id}
                value={period}
                expanded={expandedId === period.id}
                usedYears={usedYearsForThisPeriod}
                onExpandedChange={(expanded) => handleExpandedChange(period.id, expanded)}
                onDelete={() => handleDeletePeriod(index)}
                onChange={(updatedPeriod) => handlePeriodChange(index, updatedPeriod)}
                autoBooking={autoBooking}
                hasError={
                  !isValid && shouldShowErrors ? 'Please fill in all required fields' : false
                }
              />
            )
          })}

          {periods.length === 0 && (
            <Box
              sx={{
                p: 1,
                textAlign: 'center',
                color: 'text.secondary'
              }}
            >
              No licensing periods configured yet.
            </Box>
          )}

          <Box
            sx={{
              position: 'sticky',
              bottom: '.75rem',
              pt: 1.75,
              mt: 'auto',
              zIndex: 1
            }}
          >
            <Button
              variant="dashed"
              fullWidth
              startIcon={<AddIcon />}
              onClick={handleAddPeriod}
              disabled={addButtonDisabled}
            >
              Add Period
            </Button>
          </Box>
        </Stack>
      </Scrollbar>
    </Box>
  )
}
